import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, API_ENDPOINTS } from '../config/apiClient';

export interface User {
  id: number;
  username: string;
  email: string;
  firstname?: string;
  lastname?: string;
  role?: {
    id: number;
    name: string;
    type: string;
  };
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (code: string, password: string, passwordConfirmation?: string) => Promise<void>;
  logout: () => void;
  /** Call this whenever any API responds with 401 — clears auth and redirects to /login */
  handleUnauthorized: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('strapiToken');
    localStorage.removeItem('strapiUser');
  }, []);

  /** Clears auth state and redirects to /login. Call on any 401 API response. */
  const handleUnauthorized = useCallback(() => {
    clearAuth();
    navigate('/login', { replace: true, state: { sessionExpired: true } });
  }, [clearAuth, navigate]);

  // Check if user is already logged in on mount — verify token is still valid
  useEffect(() => {
    const storedToken = localStorage.getItem('strapiToken');
    const storedUser = localStorage.getItem('strapiUser');

    if (storedToken && storedUser) {
      // Validate token against the server before trusting localStorage
      fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.ME}`, {
        headers: { Authorization: `Bearer ${storedToken}` },
      })
        .then((res) => {
          if (res.status === 401 || res.status === 403) {
            // Token is invalid/expired — clear it silently
            console.warn('Stored token is no longer valid. Clearing auth state.');
            clearAuth();
          } else {
            // Token is valid — restore session from localStorage
            try {
              setToken(storedToken);
              setUser(JSON.parse(storedUser));
            } catch (error) {
              console.error('Error restoring auth state:', error);
              clearAuth();
            }
          }
        })
        .catch(() => {
          // Network error — restore from localStorage optimistically so app
          // still works offline / while server is starting up
          try {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
          } catch {
            clearAuth();
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: username,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message?.message || 'Login failed');
      }

      const data = await response.json();
      const { jwt, user: userData } = data;

      // Fetch user details with role populated
      let userWithRole = userData;
      try {
        const userDetailsResponse = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.AUTH.ME}?populate[role]=true`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (userDetailsResponse.ok) {
          const userDetails = await userDetailsResponse.json();
          // Handle Strapi v5 response structure
          const userAttrs = userDetails.attributes || userDetails;
          const roleData = userAttrs.role?.data || userAttrs.role;
          
          userWithRole = {
            ...userData,
            role: roleData?.attributes || roleData,
          };
          
          console.log('User with role:', userWithRole);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        // Continue without role if fetch fails
      }

      setToken(jwt);
      setUser(userWithRole);

      // Store in localStorage for persistence
      localStorage.setItem('strapiToken', jwt);
      localStorage.setItem('strapiUser', JSON.stringify(userWithRole));
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const signup = async (username: string, email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REGISTER}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          email: email,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message?.message || errorData.error?.message || 'Signup failed');
      }

      const data = await response.json();
      const { jwt, user: userData } = data;

      // Fetch user details with role populated
      let userWithRole = userData;
      try {
        const userDetailsResponse = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.AUTH.ME}?populate[role]=true`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (userDetailsResponse.ok) {
          const userDetails = await userDetailsResponse.json();
          // Handle Strapi v5 response structure
          const userAttrs = userDetails.attributes || userDetails;
          const roleData = userAttrs.role?.data || userAttrs.role;
          
          userWithRole = {
            ...userData,
            role: roleData?.attributes || roleData,
          };
          
          console.log('User with role:', userWithRole);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        // Continue without role if fetch fails
      }

      setToken(jwt);
      setUser(userWithRole);

      // Store in localStorage for persistence
      localStorage.setItem('strapiToken', jwt);
      localStorage.setItem('strapiUser', JSON.stringify(userWithRole));
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.FORGOT_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message?.message || 
          errorData.error?.message || 
          'Failed to send password reset email. Please check the email address.'
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  };

  const resetPassword = async (code: string, password: string, passwordConfirmation?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.RESET_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          password,
          passwordConfirmation: passwordConfirmation || password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message?.message || 
          errorData.error?.message || 
          'Failed to reset password. The link may have expired.'
        );
      }

      const data = await response.json();
      const { jwt, user: userData } = data;

      // Fetch user details with role populated
      let userWithRole = userData;
      try {
        const userDetailsResponse = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.AUTH.ME}?populate[role]=true`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (userDetailsResponse.ok) {
          const userDetails = await userDetailsResponse.json();
          // Handle Strapi v5 response structure
          const userAttrs = userDetails.attributes || userDetails;
          const roleData = userAttrs.role?.data || userAttrs.role;
          
          userWithRole = {
            ...userData,
            role: roleData?.attributes || roleData,
          };
          
          console.log('User with role:', userWithRole);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        // Continue without role if fetch fails
      }

      setToken(jwt);
      setUser(userWithRole);

      // Store in localStorage for persistence
      localStorage.setItem('strapiToken', jwt);
      localStorage.setItem('strapiUser', JSON.stringify(userWithRole));

      return data;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    signup,
    logout,
    forgotPassword,
    resetPassword,
    handleUnauthorized,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
